/**
 * 关注公众号 CTA 卡片（阶段感知文案）
 *
 * 设计原则：
 *  - 文案随阶段变化，每个阶段给一个具体的"关注能换到什么、什么时候到货"
 *  - 二维码占位符；正式上线前替换为「卓越方法星球」服务号活码图片
 *  - 视觉风格：紫白渐变 + 二维码图卡 + 卖点列表，跟主品牌色一致
 *
 * 使用场景：
 *  - 文章详情页文末（必现，第 1 篇文章后就转化）
 *  - StageMainBlock 末尾（首页阶段感知广告位）
 *  - PDF 报告页尾（已有，可考虑替换）
 */
import type { ArticleStage } from '@/data/articles'

interface BenefitCopy {
  headline: string
  bullets: string[]
}

/**
 * 文案设计原则（用户审定后定稿）：
 *  - 公众号的核心价值是：① 出分当晚第一时间提醒 ② 各阶段需要注意的事项 ③ 志愿测算工具开放通知
 *  - 不承诺还未制作的 PDF 手册类内容
 *  - 不承诺加群、答谢券类需要后端配合的兑现物
 *  - 一句话给"为什么要关注"的真实理由，不画大饼
 */
const STAGE_COPY: Record<ArticleStage, BenefitCopy> = {
  pre_exam: {
    headline: '出分那一刻，第一时间收到提醒',
    bullets: [
      '出分当晚第一时间推送查分入口',
      '志愿测算工具开放即时通知（避免错过填报窗口）',
      '考前 / 出分后 / 填报期，每个关键节点的注意事项推送'
    ]
  },
  near_exam: {
    headline: '出分那一刻，第一时间收到提醒',
    bullets: [
      '出分当晚 22:00 前推送查分入口',
      '志愿测算工具开放即时通知',
      '填报关键节点提醒（截止前 48 / 12 / 1 小时三次提醒）'
    ]
  },
  in_exam: {
    headline: '考完休息，出分当晚我们准时在这里',
    bullets: [
      '考试期间不打扰，全部科目结束后再推送',
      '出分当晚第一时间推送查分入口',
      '填报关键节点提醒'
    ]
  },
  wait_score: {
    headline: '分数已出，等官方分段表开放测算',
    bullets: [
      '官方分段表发布后第一时间提醒',
      '2026 版志愿测算开放即时通知',
      '填报关键节点提醒'
    ]
  },
  after_score: {
    headline: '分数已出，等官方分段表开放测算',
    bullets: [
      '官方分段表发布后第一时间提醒',
      '2026 版志愿测算开放即时通知',
      '填报关键节点提醒'
    ]
  },
  fill_window: {
    headline: '志愿填报关键期，3 次截止提醒不漏',
    bullets: [
      '截止前 48 / 12 / 1 小时三次提醒',
      '常见纠结场景的家长经验合集',
      '录取放榜后第一时间推送结果'
    ]
  },
  after_admission: {
    headline: '录取后这两个月，我们继续陪跑',
    bullets: [
      '高一适应阶段的注意事项推送',
      '学长经验合集（出分后整理）',
      '明年志愿测算开放时，提醒你回来回填真实结果'
    ]
  }
}

export function FollowOACard({
  stage = 'pre_exam',
  compact = false
}: {
  stage?: ArticleStage
  compact?: boolean
}) {
  const copy = STAGE_COPY[stage]

  return (
    <div className="card bg-gradient-to-br from-brand-50 to-white border border-brand-100">
      <div className="flex items-start gap-4">
        {/* 公众号二维码（长按识别） */}
        <div className="shrink-0">
          <div className="w-24 h-24 rounded-xl bg-white border border-brand-200 overflow-hidden shadow-sm">
            <img
              src="/oa-qr.png"
              alt="关注公众号 - 卓越方法星球"
              className="w-full h-full object-contain"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="mt-1.5 text-[10px] text-ink-500 text-center leading-tight">
            长按识别
          </div>
        </div>

        {/* 文案 */}
        <div className="flex-1 min-w-0">
          <div className="text-h3 text-ink-900 mb-2 leading-snug">{copy.headline}</div>
          {!compact && (
            <ul className="space-y-1.5">
              {copy.bullets.map((b, i) => (
                <li
                  key={i}
                  className="text-ink-700 text-sm leading-relaxed flex items-start gap-1.5"
                >
                  <span className="text-brand-600 shrink-0 mt-0.5">·</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 text-[11px] text-ink-400 leading-tight">
            服务号「卓越方法星球」· 邹城市卓越教育科技有限公司
          </div>
        </div>
      </div>
    </div>
  )
}
